import type { ConfiguracaoJogo, Cor, LadoAutomatico } from '../types';
import { NIVEIS_PADRAO } from './niveis';

export { NIVEIS_PADRAO };

export function ehModoDuelo(modo: ConfiguracaoJogo['modo']): boolean {
  return modo === 'duelo-motores' || modo === 'duelo-modelos' || modo === 'benchmark';
}

export function ladoAutomaticoDaCor(config: ConfiguracaoJogo, cor: Cor): LadoAutomatico | null {
  if (cor === 'w' && config.brancasAuto) return config.brancasAuto;
  if (cor === 'b' && config.pretasAuto) return config.pretasAuto;

  // Modos legados: humano vs um automático
  if (config.modo === 'local') return null;
  const corMaquina: Cor = config.corJogador === 'w' ? 'b' : 'w';
  if (cor !== corMaquina) return null;

  if (config.modo === 'modelo' && config.modeloPublicoId) {
    return {
      tipo: 'modelo',
      nome: cor === 'w' ? config.nomeBrancas : config.nomePretas,
      modeloPublicoId: config.modeloPublicoId,
      estilo: config.estilo,
      nivel: config.nivel
    };
  }

  return {
    tipo: config.motorLocal === 'lc0' ? 'lc0' : 'stockfish',
    nome: cor === 'w' ? config.nomeBrancas : config.nomePretas,
    nivel: config.nivel
  };
}

export function ambosAutomaticos(config: ConfiguracaoJogo): boolean {
  return Boolean(ladoAutomaticoDaCor(config, 'w') && ladoAutomaticoDaCor(config, 'b'));
}

export function rotuloLado(lado: LadoAutomatico): string {
  if (lado.nome) return lado.nome;
  if (lado.tipo === 'modelo') return 'Modelo';
  if (lado.tipo === 'lc0') return `Leela · ${lado.nivel ?? '2000'}`;
  return `Stockfish · ${lado.nivel ?? '2000'}`;
}

/** Localiza o participante A ou B na config atual (mesmo após inverter cores). */
export function ladoDoPapel(config: ConfiguracaoJogo, papel: 'A' | 'B'): LadoAutomatico | null {
  for (const lado of [config.brancasAuto, config.pretasAuto]) {
    if (lado?.papel === papel) return lado;
  }
  // Fallback legado: A = brancas da série / primeiro nome
  if (papel === 'A') return config.brancasAuto ?? null;
  return config.pretasAuto ?? null;
}

export function nomeCurtoTipo(tipo: LadoAutomatico['tipo']): string {
  if (tipo === 'lc0') return 'Leela';
  if (tipo === 'modelo') return 'Modelo';
  return 'Stockfish';
}

/** Nome exibido do vencedor: algoritmo, modelo ou rótulo do lado humano. */
export function nomeDoVencedor(config: ConfiguracaoJogo, vencedor: Cor | null): string | null {
  if (!vencedor) return null;
  const lado = ladoAutomaticoDaCor(config, vencedor);
  if (lado) {
    if (lado.tipo === 'lc0') return `Leela Chess Zero${lado.nivel ? ` (${lado.nivel})` : ''}`;
    if (lado.tipo === 'stockfish') return `Stockfish 18${lado.nivel ? ` (${lado.nivel})` : ''}`;
    return lado.nome || 'Modelo';
  }
  // Humano naquela cor
  const nome = vencedor === 'w' ? config.nomeBrancas : config.nomePretas;
  if (nome && nome !== 'Você') return nome;
  return 'jogador';
}

export function ladoEhHumano(config: ConfiguracaoJogo, cor: Cor): boolean {
  return ladoAutomaticoDaCor(config, cor) === null && config.modo !== 'local';
}

export function haHumanoNaPartida(config: ConfiguracaoJogo): boolean {
  if (config.modo === 'local' || config.modo === 'online') return true;
  if (ehModoDuelo(config.modo) || config.apenasAssistir) return false;
  return true;
}
